import { useContext } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { NavContext, NavRoutes, UserContext, SettingsContext } from '../contexts';
import { RolePage, UserInRolePage, CustomerRequestPage, HistoryPage, NotFoundPage, ForbiddenPage, NoSettingsPage } from '../pages';
import { ApplicationRoles } from '../../helpers';
import { useIsAuthenticated } from '@azure/msal-react';

const ProtectedRoute = (props) => {
    const isAuthenticated: boolean = useIsAuthenticated();
    const { userInfo, isUserInRole, isUserInRoles } = useContext(UserContext);
    const { areAppSettingsValidS4, areAppSettingsValidECC } = useContext(SettingsContext);

    const allowRoute = (path) => {
        if (!isAuthenticated)
            return false;

        if (userInfo.roles !== null && userInfo.roles.length > 0) {
            if (isUserInRole(ApplicationRoles.Administrator))
                return true;

            switch (path) {
                case NavRoutes.MyDesk:
                case NavRoutes.Logout:
                case NavRoutes.NotFound:
                case NavRoutes.Forbidden:
                    return true;

                case NavRoutes.NewRequest:
                    return isUserInRoles([
                        ApplicationRoles.AOFOpener,
                        ApplicationRoles.BossApprover
                    ]);

                case NavRoutes.EditRequest:
                    return isUserInRoles([
                        ApplicationRoles.AOFOpener,
                        ApplicationRoles.BossApprover,
                        ApplicationRoles.CreditRisk,
                        ApplicationRoles.CreditRiskDirector,
                        ApplicationRoles.LogisticDocumentation,
                        ApplicationRoles.LogisticCustomerService,
                        ApplicationRoles.LogisticPlanning,
                        ApplicationRoles.LogisticPlanningPerfumes,
                        ApplicationRoles.AccountReceivable,
                        ApplicationRoles.ViewOnlyJWA,
                        ApplicationRoles.ViewOnlyPerfumes,
                        ApplicationRoles.DataQuality,
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.ViewRequest:
                case NavRoutes.RequestHistory:
                    return isUserInRoles([
                        ApplicationRoles.AOFOpener,
                        ApplicationRoles.BossApprover,
                        ApplicationRoles.CreditRisk,
                        ApplicationRoles.CreditRiskDirector,
                        ApplicationRoles.LogisticDocumentation,
                        ApplicationRoles.LogisticCustomerService,
                        ApplicationRoles.LogisticPlanning,
                        ApplicationRoles.LogisticPlanningPerfumes,
                        ApplicationRoles.AccountReceivable,
                        ApplicationRoles.ViewOnlyJWA,
                        ApplicationRoles.ViewOnlyPerfumes,
                        ApplicationRoles.DataQuality,
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.DraftRequests:
                    return isUserInRoles([
                        ApplicationRoles.AOFOpener,
                        ApplicationRoles.BossApprover
                    ]);

                case NavRoutes.RunningRequests:
                case NavRoutes.CompletedRequests:
                    return isUserInRoles([
                        ApplicationRoles.AOFOpener,
                        ApplicationRoles.BossApprover,
                        ApplicationRoles.CreditRisk,
                        ApplicationRoles.CreditRiskDirector,
                        ApplicationRoles.LogisticDocumentation,
                        ApplicationRoles.LogisticCustomerService,
                        ApplicationRoles.LogisticPlanning,
                        ApplicationRoles.LogisticPlanningPerfumes,
                        ApplicationRoles.AccountReceivable,
                        ApplicationRoles.ViewOnlyJWA,
                        ApplicationRoles.ViewOnlyPerfumes,
                        ApplicationRoles.DataQuality,
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.Admin.Dashboard:
                    return isUserInRoles([
                        ApplicationRoles.DataQuality,
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.Admin.Roles:
                case NavRoutes.Admin.EditRole:
                case NavRoutes.Admin.AddUserInRole:
                case NavRoutes.Admin.EditUserInRole:
                    return isUserInRoles([
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.Admin.SapTablesECC:
                case NavRoutes.Admin.SapTablesS4:
                case NavRoutes.Admin.LocalTablesECC:
                case NavRoutes.Admin.LocalTablesS4:
                    return isUserInRoles([
                        ApplicationRoles.DataQuality,
                        ApplicationRoles.ProcessOwner
                    ]);

                case NavRoutes.Admin.Settings:
                    return isUserInRoles([
                        ApplicationRoles.DataQuality
                    ]);

                default:
                    return false;
            }

        }
        else
            return false;
    };

    const routeWithRules = (path, routeProps) => {
        switch (path) {
            case NavRoutes.NewRequest:
            case NavRoutes.EditRequest:
                if (!areAppSettingsValidS4 || !areAppSettingsValidECC)
                    return <Redirect to={NavRoutes.NoSettings} />;
                break;

            default:
                break;
        }

        return <Route {...props} />
    };

    if (allowRoute(props.path))
        return routeWithRules(props.path, props);
    else
        return <Redirect to={NavRoutes.Forbidden} />;
}

export const Routes = (props) => {
    const { menuItems } = useContext(NavContext);

    return (

        <Switch>

            {menuItems && menuItems.map((item, index) => {
                if (item.isProtected) {
                    return <ProtectedRoute key={index} exact path={item.routePath} component={item.component || NotFoundPage} />
                } else {
                    return <Route key={index} exact path={item.routePath} component={item.component || NotFoundPage} />
                }
            })}

            <ProtectedRoute exact path={NavRoutes.Admin.EditRole} component={RolePage} />
            <ProtectedRoute exact path={NavRoutes.Admin.AddUserInRole} component={UserInRolePage} />
            <ProtectedRoute exact path={NavRoutes.Admin.EditUserInRole} component={UserInRolePage} />

            <ProtectedRoute exact path={NavRoutes.EditRequest} component={CustomerRequestPage} />
            <ProtectedRoute exact path={NavRoutes.ViewRequest} component={CustomerRequestPage} />

            <ProtectedRoute exact path={NavRoutes.RequestHistory} component={HistoryPage} />

            <ProtectedRoute exact path={NavRoutes.NoSettings} component={NoSettingsPage} />

            <Route exact path={NavRoutes.Forbidden} component={ForbiddenPage} />

            <Route component={NotFoundPage} />

        </Switch>
    );
}